import React from "react";
import analyticsService from "../AnalyticsService";

const getReasonType = (reason) => {
  if (reason === null) return "null";
  if (reason === undefined) return "undefined";
  if (reason instanceof Error) return "error";
  return typeof reason;
};

const getReasonMessage = (reason) => {
  if (reason instanceof Error) {
    return reason.message || "promise_rejection";
  }

  if (typeof reason === "string") {
    return reason;
  }

  if (reason === null || reason === undefined) {
    return "promise_rejection";
  }

  try {
    return JSON.stringify(reason);
  } catch (error) {
    return String(reason);
  }
};

const getErrorDetails = (error, info = {}) => ({
  error_message: error?.message || "Unknown error",
  stack_trace: error?.stack || null,
  component: info?.component || info?.componentStack || "global",
  screen: globalThis?.location?.pathname,
  status_code: info?.status_code || null,
  source_url: info?.source_url || null,
  reason_type: info?.reason_type || null,
  reason_value: info?.reason_value || null,
  browser: globalThis?.navigator?.userAgent || "unknown",
  timestamp: new Date().toISOString(),
});

export const setupGlobalErrorTracking = () => {
  const onWindowError = (event) => {
    analyticsService.trackError(
      getErrorDetails(event?.error || new Error(event?.message || "window_error"), {
        component: "window.onerror",
      }),
    );
  };

  const onUnhandledRejection = (event) => {
    const reason = event?.reason;
    const reasonMessage = getReasonMessage(reason);
    const error = reason instanceof Error ? reason : new Error(reasonMessage);
    const sourceFromEvent =
      event?.filename ||
      event?.sourceURL ||
      (typeof event?.target?.src === "string" ? event.target.src : null);

    analyticsService.trackError(
      getErrorDetails(error, {
        component: "unhandledrejection",
        source_url: sourceFromEvent,
        reason_type: getReasonType(reason),
        reason_value: reasonMessage,
      }),
    );
  };

  globalThis.addEventListener("error", onWindowError);
  globalThis.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    globalThis.removeEventListener("error", onWindowError);
    globalThis.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
};

export class AnalyticsErrorBoundary extends React.Component {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, info) {
    analyticsService.trackError(getErrorDetails(error, info));
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}
